import { API } from 'aws-amplify';
import axios from 'axios'

import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '../types';
import { MaintenancejobsState } from './types';

import { Maintenancejob, EmailLog, Attachments, Job } from '@/models';
import _ from 'lodash';

/**
 * https://stackoverflow.com/questions/33289726/combination-of-async-function-await-settimeout
 *
 * @param seconds - number of seconds to wait
 */
const waitForSeconds = (seconds: number) => {
  return new Promise(resolve => setTimeout(resolve, seconds * 1000));
};

// Using API from aws-amplify was resulting in a "no current user" error
// So had to take authentication off the endpoint in cloudformation
// And use axios to make the request without authorisation so it doesn't try
// to use aws-amplify to return a user
// There may be an option to turn this feature off but I couldn't find it
const unAuthenticatedApiRequest = async (awsApi: any, endpoint: string, type = 'get', payload: any = {}) => {
  const awsApiConfig = awsApi.configure({});
  // grab the AWS api domain from the config
  const restApiUrl = awsApiConfig.endpoints[0].endpoint + endpoint;
  // console.log('restApiUrl', restApiUrl)

  if (type === 'post') {
    let options = {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: payload
    }
    return axios.post(restApiUrl, options);
  }
  else {
    let options = {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    }
    return axios.get(restApiUrl, options);
  }
}

const unAuthenticatedApiPostRequest = async (awsApi: any, endpoint: string, payload: any = {}) => {
  return unAuthenticatedApiRequest(awsApi, endpoint, 'post', payload);
}

const unAuthenticatedApiPutRequest = async (awsApi: any, endpoint: string, payload: any) => {
  const awsApiConfig = awsApi.configure({});
  // grab the AWS api domain from the config
  const restApiUrl = awsApiConfig.endpoints[0].endpoint + endpoint;
  // console.log('restApiUrl', restApiUrl)
  const options = {
    headers: {
      'Content-Type': 'application/json',
    }
  }
  return axios.put(restApiUrl, payload.body);
}

/**
 * Polls start/poll endpoints to see if a Job has completed.
 *
 * @param {string} job      Job
 * @param {string} startUrl Hit once to start Job
 *
 * @returns {Promise} Payload object
 */
const poll = async (job: Job, startUrl: string) => {
  // should put these constants into .env
  const POLL_TIMEOUT = 900; // seconds
  const POLL_INTERVAL = 2; // seconds

  const { id } = job;

  let count = 0;
  let errorMessage = '';

  // This is a bit ropey
  // Make an asynchronous call to the startUrl to kick off the pdf download/creation process
  // We need to check for errors and display back to user during the pdf download/creation
  // We IGNORE API Gateway timeout errors as that has a 30s hard limit
  // and the pdf creation can take > 30s.
  // The pdf download lambda will update the job to completed and this will return the url to the pdf
  // This bit of code will keep polling
  //    - for POLL_LIMIT * TIME_TO_WAIT seconds
  //    - OR until the job is complete
  //    - OR if the startUrl returns an error,
  //      Usually this will be pretty quick and be a pug template error as some data is not present
  //
  // Ideally the pdf generation lambda that creates the job will invoke the pdf download lambda
  // but I couldn't get the permissions working and to invoke the other lambda
  // If the invocation can be done, then the job needs to be able to handle errors during the pdf download process.
  // This will require more work on the job queue code, or maybe done using SQS if it's not overkill?

  console.log('url', `${startUrl}?token=${id}`);

  // Make an async (background) call to the startUrl
  // DO NOT USE Api.post()! USE unAuthenticatedApiPostRequest()
  // OTHERWISE UNAUTHENTIATED USERS WON'T BE ABLE TO DOWNLOAD REPORTS OR PHOTOS
  // API.post('RestAPI', `${startUrl}?token=${id}`, null)
  // use unauthenticated post request as the endpoint should require no authorisation
  unAuthenticatedApiPostRequest(API, `${startUrl}?token=${id}`)
    .catch(err => {
      // catch errors returned by pdf download
      // err.reponse will be undefined if API Gateway timeout which we will ignore
      // otherwise we want to return it so we can show back to the user
      if (err.response === undefined) {
        console.log('Ignore API Gateway timeout');
      } else {
        errorMessage = err.response.data.message;
      }
    });

  // Keep polling until the job is complete, or time limit is reached
  while ((count * POLL_INTERVAL) < POLL_TIMEOUT) {
    count++;

    if (errorMessage) {
      // for PDFs this error is likely to be from the Pug template
      throw new Error(errorMessage);
    }

    // const pollResult = await API.get('RestAPI', `/jobs/${id}`, null);
    const response = await unAuthenticatedApiRequest(API, `/jobs/${id}`);
    const pollResult = response.data

    console.log(`Poll attempt #${count}`, pollResult);

    if (pollResult.completed === true) {
      if (pollResult.payload === undefined) {
        throw new Error('No Job payload');
      }
      return pollResult.payload;
    }

    await waitForSeconds(POLL_INTERVAL);
  }

  throw new Error('AWS Lambda time limit exceeded.');
};

/**
 * Download an attachment (PDF/ZIP) using a Job (polling).
 *
 * @param {string} generateEndpoint Hit first, returns a Job
 * @param {string} downloadEndpoint Hit second, starts processing
 *
 * @returns {Promise} Attachment S3 Key
 */
const maintenancejobAttachmentJob = async (generateEndpoint: string, downloadEndpoint: string) => {
  // The first API call returns a Job
  // return API.post('RestAPI', generateEndpoint, null)
  // use unauthenticated post request as the endpoint should require no authorisation
  return unAuthenticatedApiPostRequest(API, generateEndpoint)
    .then((response) => new Job(response.data))
    .then((job: Job) => poll(job, downloadEndpoint))
    .then((payload) => {
      if (payload.file === undefined) {
        throw new Error('Missing Attachment Key')
      }
      return payload.file;
    });
};

/**
 * Download an attachment (PDF/ZIP) using a Job (polling).
 *
 * @param {string} generateEndpoint Hit first, returns a Job
 * @param {string} downloadEndpoint Hit second, starts processing
 *
 * @returns {Promise} Attachment S3 Key
 */
const attachmentJob = async (generateEndpoint: string, downloadEndpoint: string) => {
  // The first API call returns a Job
  // return API.post('RestAPI', generateEndpoint, null)
  // use unauthenticated post request as the endpoint should require no authorisation
  return unAuthenticatedApiPostRequest(API, generateEndpoint)
    .then((response) => new Job(response.data))
    .then((job: Job) => poll(job, downloadEndpoint))
    .then((payload) => {
      if (payload.file === undefined) {
        throw new Error('Missing Attachment Key')
      }
      return payload.file;
    });
};

const updateMaintenancejobInMaintenancejobsList = (store: ActionContext<MaintenancejobsState, any>, maintenancejob: Maintenancejob): Maintenancejob[] => {
  // Get Maintenancejob index
  const maintenancejobIndex = store.state.list.findIndex(r => r._uuid === maintenancejob._uuid);

  if (maintenancejobIndex == -1) {
    return store.state.list;
  }

  // Update Report in Reports list
  return [
    ...store.state.list.slice(0, maintenancejobIndex),
    maintenancejob,
    ...store.state.list.slice(maintenancejobIndex + 1)
  ];
};


/**
 * Actions
 *
 * Actions are similar to mutations, the differences being that:
 * - Instead of mutating the state, actions commit mutations.
 * - Actions can contain arbitrary asynchronous operations.
 * - Actions are triggered with the store.dispatch method e.g. `store.dispatch('getSettings')`
 */
export const actions: ActionTree<MaintenancejobsState, RootState> = {

  /**
     * Get all maintenancejobs
     * 
     * @param {MaintenancejobsState} store
     * @param filters
     * @returns All Maintenancejobs
     */
  async getMaintenancejobs(store: ActionContext<MaintenancejobsState, any>): Promise<Maintenancejob[]> {
    const options = {
      response: true, // OPTIONAL (return the entire Axios response object instead of only response.data)
    };
    if (store.state.search) {
      _.set(options, 'queryStringParameters.search', store.state.search);
    }

    // Filter on selected customer
    if (store.state.filterCustomer) {
      _.set(options, 'queryStringParameters.customer', store.state.filterCustomer);
    }
    // Filter on selected contractor
    if (store.state.filterContractor) {
      _.set(options, 'queryStringParameters.contractor', store.state.filterContractor);
    }
    // Filter on selected status
    if (store.state.filterStatus) {
      _.set(options, 'queryStringParameters.status', store.state.filterStatus);
    }
    store.commit('app/addRequest', 'getMaintenancejobs', { root: true });

    return API.get('RestAPI', `/maintenancejobs`, options)
      .then((response) => response.data.map((x: Partial<Maintenancejob>) => new Maintenancejob(x)))
      .then((maintenancejob: Maintenancejob[]) => {
        store.commit('setMaintenancejobs', maintenancejob);
        return maintenancejob;
      })
      .finally(() => store.commit('app/removeRequest', 'getMaintenancejobs', { root: true }));
  },
  /**
     * Get all maintenancejobs
     * 
     * @param {MaintenancejobsState} store
     * @param filters
     * @returns All Maintenancejobs
     */
  async getMaintenancejobsforinvoice(store: ActionContext<MaintenancejobsState, any>): Promise<Maintenancejob[]> {
    const options = {
      response: true, // OPTIONAL (return the entire Axios response object instead of only response.data)
    };
    if (store.state.search) {
      _.set(options, 'queryStringParameters.search', store.state.search);
    }

    // Filter on selected customer
    if (store.state.filterCustomer) {
      _.set(options, 'queryStringParameters.customer', store.state.filterCustomer);
    }
    // Filter on selected contractor
    if (store.state.filterContractor) {
      _.set(options, 'queryStringParameters.contractor', store.state.filterContractor);
    }
    // Filter on selected status
    if (store.state.invoiceFilterStatus) {
      _.set(options, 'queryStringParameters.status', store.state.invoiceFilterStatus);
    }
    store.commit('app/addRequest', 'getMaintenancejobsforinvoice', { root: true });

    return API.get('RestAPI', `/maintenancejobs`, options)
      .then((response) => response.data.map((x: Partial<Maintenancejob>) => new Maintenancejob(x)))
      .then((maintenancejob: Maintenancejob[]) => {
        store.commit('setMaintenancejobs', maintenancejob);
        return maintenancejob;
      })
      .finally(() => store.commit('app/removeRequest', 'getMaintenancejobsforinvoice', { root: true }));
  },
  /**
   * Get one Maintenancejob
   * 
   * @param {MaintenancejobsState} store 
   * @param id 
   * @returns individual Maintenancejob
   */
  async getMaintenancejob(store: ActionContext<MaintenancejobsState, any>, id) {
    // New Maintenancejob
    if (id === 'new') {
      const maintenancejob = new Maintenancejob();
      store.commit('setCurrent', maintenancejob);
      return maintenancejob;
    }
    store.commit('app/addRequest', 'getMaintenancejob', { root: true });

    return API.get('RestAPI', `/maintenancejobs/${id}`, null)
      .then((response) => new Maintenancejob(response))
      .then((maintenancejob: Maintenancejob) => {
        store.commit('setCurrent', maintenancejob);
        return maintenancejob;
      })
      .finally(() => store.commit('app/removeRequest', 'getMaintenancejob', { root: true }));
  },

  /**
   * Add Maintenancejob
   * 
   * @param {MaintenancejobsState} store 
   * @param maintenancejob Maintenancejob
   */
  async addMaintenancejob(store: ActionContext<MaintenancejobsState, any>, maintenancejob: Maintenancejob): Promise<void> {
    try {
      store.commit('app/addRequest', 'addMaintenancejob', { root: true });
      return API.post('RestAPI', '/maintenancejobs', { body: maintenancejob.toJSON() })
        .then((data) => new Maintenancejob(data))
        .then((maintenancejobfromserver: Maintenancejob) => {
          store.commit('resetUnsavedChanges');
        })
        .finally(() => store.commit('app/removeRequest', 'addMaintenancejob', { root: true }));
    }
    catch (err) {
      console.error(err);
      throw new Error(err.message);
    }
  },

  /**
   * Delete Maintenancejob
   * 
   * @param {MaintenancejobsState} store 
   * @param maintenancejob Maintenancejob
   */
  async deleteMaintenancejob(store: ActionContext<MaintenancejobsState, any>, maintenancejob: Maintenancejob): Promise<void> {
    store.commit('app/addRequest', 'deleteMaintenancejob', { root: true });
    return API.del('RestAPI', `/maintenancejobs/${maintenancejob.id}`, {})
      .then(() => {
        store.commit('resetUnsavedChanges');
        store.commit('setMaintenancejobs', _.remove(store.state.list, (c: Maintenancejob) => c.id != maintenancejob.id));
      })
      .finally(() => store.commit('app/removeRequest', 'deleteMaintenancejob', { root: true }));
  },


  /**
   * Update given Maintenancejob details
   * 
   * @param {MaintenancejobsState} store 
   * @param maintenancejob Maintenancejob
   */
  async updateMaintenancejob(store: ActionContext<MaintenancejobsState, any>, maintenancejob: Maintenancejob): Promise<void> {
    store.commit('app/addRequest', 'updateMaintenancejob', { root: true });
    return API.put('RestAPI', `/maintenancejobs/${maintenancejob.id}`, { body: maintenancejob.toJSON() })
      .then((data) => new Maintenancejob(data))
      .then((maintenancejob: Maintenancejob) => {
        store.commit('setCurrent', maintenancejob);
        store.commit('resetUnsavedChanges');
      })
      .finally(() => store.commit('app/removeRequest', 'updateMaintenancejob', { root: true }));
  },
  /**
 * Update given Maintenancejob details
 * 
 * @param {MaintenancejobsState} store 
 * @param maintenancejob Maintenancejob
 */
  async updateMaintenancejobPayment(store: ActionContext<MaintenancejobsState, any>, maintenancejob: Maintenancejob): Promise<void> {
    store.commit('app/addRequest', 'updateMaintenancejobPayment', { root: true });
    return API.put('RestAPI', `/update-payment-maintenancejob/${maintenancejob.id}/${maintenancejob.payment}`, {})
      .then((data) => new Maintenancejob(data))
      .then((updatemaintenancejob: Maintenancejob) => {
        store.commit('setCurrent', maintenancejob);
        let index = store.state.list.findIndex((r: Maintenancejob) => r.id === updatemaintenancejob.id);
        if (index >= 0) {
          store.state.list[index] = updatemaintenancejob;
          store.commit('setMaintenancejobs', store.state.list);
        }
        store.commit('resetUnsavedChanges');
      })
      .finally(() => store.commit('app/removeRequest', 'updateMaintenancejobPayment', { root: true }));
  },

  /**
   * Sets any deep maintenancejob property and increments the "unsaved changes" count
   * @param store
   * @param data
   */
  setMaintenancejobDeep(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setMaintenancejobDeep', data);
    store.commit('addUnsavedChange');
  },


  /**
   * Generate "Maintenancejob" PDF (Uses Job polling)
   *
   * @todo not DRY! See generateMaintenancejobPdf()
   */
  async generateMaintenancejobPdf(store: ActionContext<MaintenancejobsState, any>, payload: { maintenancejob: Maintenancejob, downloadType: string }): Promise<any> {
    const { attachments, id } = payload.maintenancejob;

    try {
      // Does this attachment exist in Vuex..?
      if (payload.downloadType) {
        if (attachments[payload.downloadType as keyof Attachments]) {
          return attachments[payload.downloadType as keyof Attachments];
        }
      }
      const generateEndpoint = `/maintenancejobs/${id}/generate/${payload.downloadType}`;
      const downloadEndpoint = `/maintenancejobs/${id}/download/${payload.downloadType}`;

      const key = await attachmentJob(generateEndpoint, downloadEndpoint);

      if (key) {
        // Set the S3 Key for the attachment
        if (payload.downloadType) {
          payload.maintenancejob.attachments[payload.downloadType as keyof Attachments] = key;
        }

        // Update Report in Vuex
        store.commit("setMaintenancejobs", updateMaintenancejobInMaintenancejobsList(store, payload.maintenancejob));
        if (payload.maintenancejob.id === this.state.maintenancejobs.current.id) {
          store.commit("setCurrent", payload.maintenancejob);
        }

        return key;
      } else {
        throw new Error('Unable to generate Report PDF');
      }
    }
    catch (err) {
      console.error(err);
      throw new Error(err.message);
    }
  },

  /**
 * Set the page for the Reports query
 *
 * @param store
 * @param data
 */
  resetCurrentPage(store: ActionContext<MaintenancejobsState, any>): void {
    store.commit('setCurrentPage', 1);
  },

  /**
  * Set the contractor for the Maintenancejob query.
  *
  * @param store
  * @param data
  */
  setFilterCustomer(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setFilterCustomer', data);
  },
  /**
  * Set the contractor for the Maintenancejob query.
  *
  * @param store
  * @param data
  */
  setFilterContractor(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setFilterContractor', data);
  },
  /**
 * Set the contractor for the Maintenancejob query.
 *
 * @param store
 * @param data
 */
  setFilterStatus(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setFilterStatus', data);
  },
  /**
* Set the contractor for the Maintenancejob query.
*
* @param store
* @param data
*/
  setFilterInvoiceStatus(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setFilterInvoiceStatus', data);
  },


  /**
  * Set the search text for the Maintenancejob query.
  *
  * @param store
  * @param data
  */
  setSearch(store: ActionContext<MaintenancejobsState, any>, data): void {
    store.commit('setSearch', data);
  },

  /**
 * Send email
 * 
 * @param {DiaryState} store 
 * @param email: Email 
 * @returns response
 */
  async sendEmail(store: ActionContext<MaintenancejobsState, any>, email: EmailLog): Promise<string> {
    store.commit('app/addRequest', 'sendEmail', { root: true });
    let jsonBody = email.toJSON();
    return API.post('RestAPI', '/email/emailwithattachments', { body: jsonBody })
      .finally(() => store.commit('app/removeRequest', 'sendEmail', { root: true }));
  },

}
